import React, { useContext, useEffect, useState } from 'react';
import AffAircraftSummaryModal from './AffAircraftSummaryModal';
import AffAircraftDetailModal from './AffAircraftDetailModal';
import { AffAircraftEntity } from './affServiceAdapter';
import { LayerDrawerContext } from '../../../easymap/layerDrawer/LayerDrawerContext';
import { usePermissions } from '../../../context/PermissionContext';
import { AFF_FEATURE, AFF_EXPLORE_OP } from '../../../shared/affPermissions';
import {
  AFF_SHOW_AIRCRAFT_EVENT,
  dispatchAffShowTrack,
  AffAircraftLevel,
} from './affTrackEvent';

/**
 * Renders the aircraft summary/detail modals for whoever asks.
 *
 * Both the explorer's per-row View button and a click on the aircraft's map
 * marker raise AFF_SHOW_AIRCRAFT_EVENT, so the two routes reach the same
 * modals. Previously the map had its own popup that dumped every raw property
 * as `key: value`.
 *
 * Living outside the explorer matters: an aircraft can be clicked on the map
 * with the explorer closed, and the detail view should still be available.
 *
 * #866 — the summary is aff.read (it is built from the live feed the marker
 * already holds). The detail view, with its activity calendar and Show Track,
 * is aff.explore: without it there is no More Info, and a request for the
 * detail level opens the summary instead.
 */
const AffAircraftModalHost: React.FC = () => {
  const { canDo } = usePermissions();
  const canExplore = canDo(AFF_FEATURE, AFF_EXPLORE_OP);
  const [aircraft, setAircraft] = useState<AffAircraftEntity | null>(null);
  const [detailed, setDetailed] = useState(false);
  // Size of the marker that was clicked, so the panel clears it by the same gap
  // whatever the aircraft type (#805). Absent when opened from the explorer.
  const [iconHeight, setIconHeight] = useState<number | undefined>(undefined);
  /**
   * #866 — the modal shows the aircraft as it was when opened; it is not
   * refreshed. Stated as a time rather than kept live: live would need its own
   * poll (the map layer and grid refresh on their own cadences and publish
   * nothing), and a time is enough for Age and Stale to read truthfully.
   */
  const [asOf, setAsOf] = useState<number | undefined>(undefined);

  // Read the context directly rather than through useLayerDrawer: that hook
  // throws outside the provider, and this host is deliberately mountable on its
  // own. No map simply means the panel is not anchored (#805 axis 1 is
  // optional) — it is not a missing dependency to fail on.
  const drawer = useContext(LayerDrawerContext);
  const map = drawer?.map ?? null;

  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<{
        aircraft?: AffAircraftEntity;
        iconHeight?: number;
        level?: AffAircraftLevel;
      }>).detail;
      if (!detail?.aircraft) return;
      setAircraft(detail.aircraft);
      setAsOf(Date.now());
      setIconHeight(detail.iconHeight);
      // The level travels with each event rather than sticking from the last
      // one, so a map click after an explorer View still opens at summary.
      setDetailed(detail.level === 'detail');
    };

    window.addEventListener(AFF_SHOW_AIRCRAFT_EVENT, handler);
    return () => window.removeEventListener(AFF_SHOW_AIRCRAFT_EVENT, handler);
  }, []);

  const close = () => {
    setAircraft(null);
    setDetailed(false);
  };

  if (!aircraft) return null;

  if (!detailed || !canExplore) {
    return (
      <AffAircraftSummaryModal
        aircraft={aircraft}
        onClose={close}
        onMoreInfo={canExplore ? () => setDetailed(true) : undefined}
        map={map}
        anchorIconHeight={iconHeight}
        asOf={asOf}
        anchor={
          typeof aircraft.lon === 'number' && typeof aircraft.lat === 'number'
            ? { lng: aircraft.lon, lat: aircraft.lat }
            : undefined
        }
      />
    );
  }

  return (
    <AffAircraftDetailModal
      aircraft={aircraft}
      onClose={close}
      asOf={asOf}
      onShowTracks={(esn, span) => {
        // `name` is the registration Selkirk reports (aff_aircraft_history).
        const registration = typeof aircraft?.name === 'string' && aircraft.name.trim() !== ''
          ? aircraft.name.trim()
          : undefined;
        dispatchAffShowTrack({ esn, name: aircraft?.name, registration, ...span });
        close();
      }}
    />
  );
};

export default AffAircraftModalHost;
